import React, { useMemo, useState } from "react";
import { Row, Col } from "reactstrap";
import CustomTable from "../../widgets/table";
import { ArrowLeftOutlined, SettingOutlined, DownloadOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { CustomButton } from "../../widgets";
import { FIELD_TYPES, FieldItem } from "../../widgets/fields";

// Types
type Field = {
  id: string;
  field_name: string;
  field_selected_value: string;
};

type RecordItem = {
  id: string;
  fields: Field[];
};

type FormData = RecordItem[];

type TemplateForm = {
  form_id: string;
  section_title: string;
};

type Template = {
  title: string;
  forms: TemplateForm[];
};

interface GenerateReportProps {
  reportData: Record<string, FormData>;
  templateData: Template;
  columnsPerPage?: number;
  rowsPerPage?: number;
  projectData?: any;
  filterData?: any;
  onClose?: () => void;
}

interface DetailedField {
  field_id: string;
  field_name: string;
  field_value: string;
}

interface TableSlice {
  headers: string[];
  rows: (string | number)[][];
  fieldIds: string[];
  tableIndex: number;
}

interface FormSection {
  form_id: string;
  section_title: string;
  detailedFields: DetailedField[];
  allTableFieldIds: string[];
  allTableRows: any[];
  recordCount: number;
}

interface TableConfig {
  columns: number;
  rows: number;
}

const GenerateReport: React.FC<GenerateReportProps> = ({
  reportData,
  templateData,
  columnsPerPage = 4,
  rowsPerPage = 20,
  projectData,
  filterData,
  onClose,
}) => {
  console.log(reportData)
  // State to track table configurations per section
  const [tableConfigs, setTableConfigs] = useState<Record<string, TableConfig[]>>({});
  const [showSettings, setShowSettings] = useState<Record<string, boolean>>({});
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Utility: Chunk array into smaller arrays
  const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // Build form sections with all table data
  const formSections: FormSection[] = useMemo(() => {
    const sections: FormSection[] = [];
    templateData?.forms?.forEach((templateForm) => {
      const formId = templateForm.form_id;
      const formData = reportData[formId];
      if (!formData || formData.length === 0) {
        return;
      }

      const records = formData;
      const recordCount = records.length;

      const fieldIdsSet = new Set<string>();
      records.forEach((record) => {
        record.fields?.forEach((field) => fieldIdsSet.add(field.id));
      });
      const allFieldIds = Array.from(fieldIdsSet);

      let detailedFields: DetailedField[] = [];
      let allTableFieldIds: string[] = [];
      let allTableRows: any[] = [];

      if (recordCount === 1) {
        const record = records[0];
        detailedFields = allFieldIds.map((fieldId) => {
          const field = record.fields?.find((f) => f.id === fieldId);
          return {
            field_id: fieldId,
            field_name: field?.field_name || "-",
            field_value: field?.field_selected_value || "-",
          };
        });
      } else {
        const repeatingFields: DetailedField[] = [];
        const nonRepeatingFieldIds: string[] = [];

        allFieldIds.forEach((fieldId) => {
          const values = records.map((record) => {
            const field = record.fields?.find((f) => f.id === fieldId);
            return field?.field_selected_value || "-";
          });

          const uniqueValues = new Set(values);
          if (uniqueValues.size === 1) {
            const firstRecord = records[0];
            const field = firstRecord.fields?.find((f) => f.id === fieldId);
            repeatingFields.push({
              field_id: fieldId,
              field_name: field?.field_name || "",
              field_value: values[0],
            });
          } else {
            nonRepeatingFieldIds.push(fieldId);
          }
        });

        detailedFields = repeatingFields;
        allTableFieldIds = nonRepeatingFieldIds;

        if (allTableFieldIds.length > 0) {
          allTableRows = records.map((record, index) => {
            const row: any = { slNo: index + 1 };
            allTableFieldIds.forEach((fieldId) => {
              const field = record.fields?.find((f) => f.id === fieldId);
              row[fieldId] = field?.field_selected_value || "-";
            });
            return row;
          });
        }
      }

      sections.push({
        form_id: formId,
        section_title: templateForm.section_title,
        detailedFields,
        allTableFieldIds,
        allTableRows,
        recordCount,
      });
    });
    return sections;
  }, [reportData, templateData]);

  // Get or initialize table configs for a section
  const getTableConfigs = (sectionId: string, totalCols: number): TableConfig[] => {
    if (tableConfigs[sectionId]) {
      return tableConfigs[sectionId];
    }

    const numTables = Math.ceil(totalCols / columnsPerPage);
    const defaultConfigs: TableConfig[] = [];
    for (let i = 0; i < numTables; i++) {
      defaultConfigs.push({
        columns: Math.min(columnsPerPage, totalCols - i * columnsPerPage),
        rows: rowsPerPage,
      });
    }

    return defaultConfigs;
  };

  // Update table configuration
  const updateTableConfig = (sectionId: string, tableIndex: number, field: 'columns' | 'rows', value: number) => {
    setTableConfigs(prev => {
      const currentConfigs = prev[sectionId] || getTableConfigs(sectionId, 0);
      const newConfigs = [...currentConfigs];

      if (!newConfigs[tableIndex]) {
        newConfigs[tableIndex] = { columns: columnsPerPage, rows: rowsPerPage };
      }

      newConfigs[tableIndex] = {
        ...newConfigs[tableIndex],
        [field]: Math.max(1, value),
      };

      return {
        ...prev,
        [sectionId]: newConfigs,
      };
    });
  };

  // Generate table slices based on configurations
  const generateTableSlices = (section: FormSection): TableSlice[] => {
    const { allTableFieldIds, allTableRows, form_id } = section;

    if (allTableFieldIds.length === 0 || allTableRows.length === 0) {
      return [];
    }

    const configs = getTableConfigs(form_id, allTableFieldIds.length);
    const slices: TableSlice[] = [];

    let colOffset = 0;
    let tableIndex = 0;

    while (colOffset < allTableFieldIds.length) {
      const config = configs[tableIndex] || { columns: columnsPerPage, rows: rowsPerPage };
      const colsToTake = Math.min(config.columns, allTableFieldIds.length - colOffset);
      const currentFieldIds = allTableFieldIds.slice(colOffset, colOffset + colsToTake);

      // Chunk rows based on config
      const rowChunks = chunkArray(allTableRows, config.rows);

      rowChunks.forEach((rowChunk) => {
        const colHeaders = currentFieldIds.map((fieldId) => {
          const formData = reportData[section.form_id];
          if (formData && formData.length > 0) {
            for (const record of formData) {
              const field = record.fields?.find((f) => f.id === fieldId);
              if (field && field.field_name) {
                return field.field_name;
              }
            }
          }
          return fieldId;
        });

        const headers = ["Sl. No", ...colHeaders];
        const rows = rowChunk.map((record) => {
          const row: (string | number)[] = [record.slNo];
          currentFieldIds.forEach((fieldId) => {
            row.push(record[fieldId] || "");
          });
          return row;
        });

        slices.push({
          headers,
          rows,
          fieldIds: currentFieldIds,
          tableIndex,
        });
      });

      colOffset += colsToTake;
      tableIndex++;
    }

    return slices;
  };

  // Sanitize text for PDF rendering (replace unsupported Unicode characters)
  const sanitizeForPDF = (text: string): string => {
    if (!text) return text;
    return text
      .replace(/→/g, "->")
      .replace(/←/g, "<-")
      .replace(/↑/g, "^")
      .replace(/↓/g, "v")
      .replace(/–/g, "-")
      .replace(/—/g, "--")
      .replace(/"/g, '"')
      .replace(/"/g, '"')
      .replace(/'/g, "'")
      .replace(/'/g, "'")
      .replace(/…/g, "...")
      .replace(/[^\x00-\x7F]/g, (char) => {
        return char.normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "?";
      });
  };

  // PDF Generation Function
  const generatePDF = () => {
    setIsGeneratingPDF(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;

      // Add title with border
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(44, 62, 80); // #2c3e50
      const title = templateData?.title || projectData?.TopicDesc || projectData?.topicdesc || "Report";
      doc.text(title, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 8;

      // Add generation date
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(127, 140, 141); // #7f8c8d
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: "center" });
      yPosition += 5;

      // Add bottom border for header
      doc.setDrawColor(41, 128, 185); // #2980b9
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 15;

      // Process each section
      formSections.forEach((section, sectionIndex) => {
        // Check if we need a new page for section header
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = margin;
        }

        // Add section title with blue background
        doc.setFillColor(0, 107, 217); // var(--primary-color, #006BD9)
        doc.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, 12, 2, 2, 'F');

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255); // white
        doc.text(section.section_title, margin + 5, yPosition + 3);
        yPosition += 15;

        // Reset text color for content
        doc.setTextColor(0, 0, 0);

        // Add detailed fields (key-value pairs) with background
        if (section.detailedFields.length > 0) {
          // Add light gray background for detailed view section
          const detailedViewStartY = yPosition;

          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");

          const fieldsPerRow = 2;
          const colWidth = (pageWidth - 2 * margin - 10) / fieldsPerRow;
          let currentCol = 0;
          let maxYInRow = yPosition;

          section.detailedFields.forEach((field, index) => {
            const xPos = margin + 5 + (currentCol * (colWidth + 5));
            let currentY = yPosition;

            // Check if we need a new page
            if (currentY > pageHeight - 30) {
              doc.addPage();
              yPosition = margin;
              currentY = yPosition;
              currentCol = 0;
              maxYInRow = yPosition;
            }

            // Field background (light gray box)
            doc.setFillColor(249, 249, 249); // #f9f9f9
            doc.setDrawColor(224, 224, 224); // #e0e0e0
            doc.setLineWidth(0.1);

            const fieldText = sanitizeForPDF(field.field_value || "-");
            const labelHeight = 5;
            const splitValue = doc.splitTextToSize(fieldText, colWidth - 6);
            const valueHeight = splitValue.length * 4;
            const totalBoxHeight = labelHeight + valueHeight + 4;

            doc.roundedRect(xPos, currentY, colWidth, totalBoxHeight, 1, 1, 'FD');

            // Field label
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(85, 85, 85); // #555
            doc.text(sanitizeForPDF(field.field_name) + ":", xPos + 3, currentY + 4);
            currentY += labelHeight + 1;

            // Field value
            doc.setFont("helvetica", "normal");
            doc.setTextColor(51, 51, 51); // #333
            doc.text(splitValue, xPos + 3, currentY + 3);

            currentY += valueHeight + 3;
            maxYInRow = Math.max(maxYInRow, currentY);

            currentCol++;
            if (currentCol >= fieldsPerRow) {
              currentCol = 0;
              yPosition = maxYInRow + 3;
            }
          });

          // Move to next row if we ended mid-row
          if (currentCol !== 0) {
            yPosition = maxYInRow + 3;
          }

          yPosition += 5;
        }

        // Add tables
        if (section.allTableFieldIds.length > 0) {
          const slices = generateTableSlices(section);

          slices.forEach((slice, sliceIndex) => {
            if (yPosition > pageHeight - 40) {
              doc.addPage();
              yPosition = margin;
            }

            // Add table title
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(102, 102, 102); // #666
            const tableTitle = `Table ${slice.tableIndex + 1}${sliceIndex > 0 ? ` - Part ${sliceIndex + 1}` : ''}`;
            doc.text(tableTitle, margin, yPosition);
            yPosition += 7;

            // Generate table
            autoTable(doc, {
              startY: yPosition,
              head: [slice.headers.map(sanitizeForPDF)],
              body: slice.rows.map(row => row.map(cell => sanitizeForPDF(String(cell)))),
              margin: { left: margin, right: margin },
              theme: 'grid',
              styles: {
                fontSize: 8,
                cellPadding: 3,
                textColor: [51, 51, 51],
                lineColor: [224, 224, 224],
                lineWidth: 0.1,
                overflow: 'linebreak',
                font: 'helvetica',
              },
              headStyles: {
                fillColor: [0, 107, 217],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: 'left',
                overflow: 'linebreak',
              },
              bodyStyles: {
                fillColor: [255, 255, 255],
                minCellHeight: 10,
              },
              alternateRowStyles: {
                fillColor: [250, 250, 250],
              },
              columnStyles: {
                0: { cellWidth: 15 }, // Sl. No column stays narrow
              },
              tableWidth: 'auto',
            });

            // Get the final Y position after the table
            yPosition = (doc as any).lastAutoTable.finalY + 10;
          });
        }

        // Add spacing between sections
        yPosition += 10;
      });

      // Save the PDF
      const fileName = `${title}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Render detailed view (key-value pairs)
  const renderDetailedView = (fields: DetailedField[]) => {
    if (fields.length === 0) return null;

    return (
      <div className="detailed-view">
        <Row>
          {fields.map((field) => (
            <Col md={6} key={field.field_id} className="detailed-field-col">
              <div className="field-wrapper">
                <strong className="field-label">{field.field_name}:</strong>
                <span className="field-value">{field.field_value || "—"}</span>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render table slices with settings
  const renderTableSlices = (section: FormSection) => {
    const slices = generateTableSlices(section);
    const sectionId = section.form_id;

    if (slices.length === 0) return null;

    const configs = getTableConfigs(sectionId, section.allTableFieldIds.length);

    return (
      <>
        {slices.map((slice, sliceIndex) => {
          const tableIndex = slice.tableIndex;
          const config = configs[tableIndex] || { columns: columnsPerPage, rows: rowsPerPage };

          // Create table props
          const tableProps = [
            {
              id: "slNo",
              label: "Sl. No",
              accessor: "slNo",
              sort: "asc" as const,
            },
            ...slice.fieldIds.map((fieldId, idx) => ({
              id: fieldId,
              label: slice.headers[idx + 1],
              accessor: fieldId,
            })),
          ];

          const tableData = slice.rows.map((row) => {
            const rowObj: any = { slNo: row[0] };
            slice.fieldIds.forEach((fieldId, idx) => {
              rowObj[fieldId] = row[idx + 1];
            });
            return rowObj;
          });

          return (
            <div key={`${sectionId}-slice-${sliceIndex}`} className="table-slice-wrapper">
              <div className="table-header-controls">
                <p className="table-part-indicator">
                  Table {tableIndex + 1}
                  {sliceIndex > 0 && ` - Part ${sliceIndex + 1}`}
                </p>

                <div className="table-settings">
                  <div className="setting-group">
                    <label>Columns:</label>
                    <input
                      type="number"
                      min="1"
                      max={section.allTableFieldIds.length}
                      value={config.columns}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === '0') return;
                        const numVal = parseInt(val);
                        if (!isNaN(numVal)) {
                          updateTableConfig(sectionId, tableIndex, 'columns', numVal);
                        }
                      }}
                    />
                  </div>

                  <div className="setting-group">
                    <label>Rows:</label>
                    <input
                      type="number"
                      min="1"
                      value={config.rows}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || val === '0') return;
                        const numVal = parseInt(val);
                        if (!isNaN(numVal)) {
                          updateTableConfig(sectionId, tableIndex, 'rows', numVal);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <CustomTable
                tableProps={tableProps}
                data={tableData}
                isSort={false}
                isSelect={false}
                isDraggable={false}
                isSearch={false}
                isFilter={false}
                showPagination={false}
                onAction={() => { }}
                getRearrangeData={() => { }}
                getFilteredData={() => { }}
                rules={[]}
                formId={null}
              />
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      <div className="top-bar generate-report-top-bar">
        <div className={window.innerWidth > 750 ? "page-header-style" : "page-header-data-style"}>
          <div className="title-container">
            <div className={window.innerWidth > 750 ? "project-title" : "project-title-mob"}>
              <ArrowLeftOutlined
                style={{ color: "#fff", fontSize: "36px", margin: "0px 10px" }}
                onClick={onClose}
              />
              <div className="title">
                {projectData?.TopicDesc || projectData?.topicdesc}
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <CustomButton
              type="secondary"
              onClick={generatePDF}
              text={"Download PDF"}
              loading={isGeneratingPDF}
              icon={<DownloadOutlined />}
            />
          </div>
        </div>
      </div>

      <div className="generate-report">
        {formSections.map((section, sectionIndex) => (
          <div key={`${section.form_id}-${sectionIndex}`} className="form-section">
            <div className="section-header">
              <h2 className="section-title">{section.section_title}</h2>
            </div>

            <div className="section-content">
              {section.detailedFields.length > 0 && (
                <div>
                  {renderDetailedView(section.detailedFields)}
                </div>
              )}

              {section.allTableFieldIds.length > 0 && (
                <div>
                  {renderTableSlices(section)}
                </div>
              )}
            </div>
          </div>
        ))}

        {formSections.length === 0 && (
          <div className="no-data">
            <p>No data available to display in this report.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default GenerateReport;